import { Dialog, Slide, Button } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import React from "react";
import Spinner from "../../components/spinner";
import MuiAlert from "@mui/material/Alert";
import Editor from "../../components/elements/Editor";
import ReactDatePicker from "react-datepicker";
import useBlogForm from "../../hooks/blog/useBlogForm";
import useBlogImage from "../../hooks/blog/useBlogImage";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const IS_REQUIRED = true;

function AddBlogs({ open, handleClose }) {
  // Use the blog form hook
  const {
    blogFormData,
    isSubmitting,
    isError,
    isSuccess,
    handleBlogFormData,
    handleBlogSeo,
    handleImageAlt,
    handleChangeEditor,
    handleSetPublishDate,
    generateSlug,
    handleSubmitBlogFormData,
    clearData,
    closeErrorMessage,
    updateImageData,
    updateThumbnailData,
    clearImageData,
    clearThumbnailData,
  } = useBlogForm({
    isEditMode: false,
    onClose: handleClose,
  });

  // Use the blog image hook for banner image
  const {
    fileInputRef,
    imagePreview,
    isUploading,
    handleOpenFileUploadDialog,
    handleUploadFile,
    handleDeleteImage: handleDeleteBanner,
  } = useBlogImage({
    onImageUploaded: (document_id, imageUrl) => {
      updateImageData(document_id, imageUrl);
    },
    onImageDeleted: clearImageData,
  });

  // Use the blog image hook for thumbnail image
  const {
    fileInputRef: thumbnailFileInputRef,
    imagePreview: thumbnailPreview,
    isUploading: isThumbnailUploading,
    handleOpenFileUploadDialog: handleOpenThumbnailUploadDialog,
    handleUploadFile: handleUploadThumbnail,
    handleDeleteImage: handleDeleteThumbnail,
  } = useBlogImage({
    onImageUploaded: (_, imageUrl) => {
      updateThumbnailData(imageUrl);
    },
    onImageDeleted: clearThumbnailData,
  });

  return (
    <Dialog
      fullScreen
      fullWidth={true}
      open={open}
      onClose={clearData}
      TransitionComponent={Transition}
      disableEnforceFocus={true}
      disableAutoFocus={true}
    >
      <div className="container_xxl px-5">
        <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-40 bg-white">
          <div>
            <IconButton onClick={clearData}>
              <ArrowBackIcon />
            </IconButton>
          </div>
          <div>
            <h1 className="heading">Add Blog</h1>
          </div>
        </div>
        <div className="form-body py-8">
          <form
            className=""
            autoComplete="off"
            onSubmit={handleSubmitBlogFormData}
          >
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="title" className="label">
                  Blog Title <small className="text-red-700">*</small>
                </label>
                <input
                  type="text"
                  required={IS_REQUIRED}
                  placeholder="Enter Blog Title"
                  value={blogFormData.title}
                  onChange={handleBlogFormData}
                  onBlur={generateSlug}
                  name="title"
                  className="input mt-4"
                />
              </div>

              <div>
                <label htmlFor="slug" className="label">
                  Blog Slug <small className="text-red-700">*</small>
                </label>
                <input
                  type="text"
                  required={IS_REQUIRED}
                  placeholder="blog-post-slug"
                  value={blogFormData.slug}
                  onChange={handleBlogFormData}
                  name="slug"
                  className="input mt-4"
                />
              </div>

              <div>
                <label htmlFor="description" className="label">
                  Blog Description <small className="text-red-700">*</small>
                </label>
                <input
                  type="text"
                  required={IS_REQUIRED}
                  placeholder="Enter Blog Description"
                  value={blogFormData.description}
                  onChange={handleBlogFormData}
                  name="description"
                  className="input mt-4"
                />
              </div>

              <div>
                <label htmlFor="published_at" className="label">
                  Blog Publish Date <small className="text-red-700">*</small>
                </label>
                <div className="w-full">
                  <ReactDatePicker
                    dateFormat="yyyy/MM/dd"
                    required={IS_REQUIRED}
                    selected={blogFormData.published_at}
                    onChange={(date) => handleSetPublishDate(date)}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="">
                <label htmlFor="Blog Title" className="label">
                  Blog Banner Image
                </label>
                <small className="text-blue-700">
                  Note: Recommended Size: 1920 x 1080 px (JPG, PNG, JPEG)
                  <br />
                  Use a wide, cinematic crop. The top and bottom will be heavily cropped on the website.
                </small>
                {isUploading ? (
                  <Spinner />
                ) : (
                  <>
                    {imagePreview && (
                      <div className="my-4">
                        <img
                          src={imagePreview}
                          alt="Blog preview"
                          className="object-contain w-[400px] h-auto border mb-2"
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={handleOpenFileUploadDialog}
                            disabled={isUploading}
                          >
                            Change Image
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteBanner}
                            startIcon={<DeleteIcon />}
                          >
                            Remove Image
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="mt-3">
                  <label htmlFor="image_alt" className="label">
                    Image Alt Text
                  </label>
                  <input
                    type="text"
                    placeholder="Image Alt Text"
                    value={blogFormData.image_alt}
                    onChange={handleImageAlt}
                    name="image_alt"
                    className="input mt-2"
                  />
                </div>

                <div className="mt-5">
                  {!imagePreview && (
                    <Button
                      type="button"
                      variant="contained"
                      color="success"
                      className="primaryBtn-contained"
                      onClick={handleOpenFileUploadDialog}
                      disabled={isUploading}
                    >
                      Add Banner Image
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleUploadFile}
                  />
                </div>
              </div>

              {/* Add Thumbnail section */}
              <div className="">
                <label htmlFor="Blog Thumbnail" className="label">
                  Blog Thumbnail
                </label>
                <small className="text-blue-700">
                  Note: Recommended Size: 1600 x 1200 px (JPG, PNG, JPEG)
                </small>
                {isThumbnailUploading ? (
                  <Spinner />
                ) : (
                  <>
                    {thumbnailPreview && (
                      <div className="my-4">
                        <img
                          src={thumbnailPreview}
                          alt="Thumbnail preview"
                          className="object-contain w-[250px] h-auto border mb-2"
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={handleOpenThumbnailUploadDialog}
                            disabled={isThumbnailUploading}
                          >
                            Change Image
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteThumbnail}
                            startIcon={<DeleteIcon />}
                          >
                            Remove Image
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="mt-5">
                  {!thumbnailPreview && (
                    <Button
                      type="button"
                      variant="contained"
                      color="success"
                      className="primaryBtn-contained"
                      onClick={handleOpenThumbnailUploadDialog}
                      disabled={isThumbnailUploading}
                    >
                      Add Thumbnail
                    </Button>
                  )}
                  <input
                    ref={thumbnailFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleUploadThumbnail}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="Blog Content" className="label">
                  Blog Content <small className="text-red-700">*</small>
                </label>
                <div className="mt-5">
                  <Editor
                    value={blogFormData?.content}
                    onChange={handleChangeEditor}
                  />
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between border-b border-gray-400">
                <h1 className="font-semibold text-2xl pb-3">
                  Search Engine Optimization
                </h1>
              </div>

              <div>
                <div className="mb-2">
                  <label className="label">Meta Title</label>
                  <input
                    type="text"
                    placeholder="Meta Title"
                    name="seo"
                    data-name="title"
                    className="input mt-4"
                    value={blogFormData?.seo?.title}
                    onChange={handleBlogSeo}
                  />
                </div>
                <div className="mb-2">
                  <label className="label">Meta Description</label>
                  <input
                    type="text"
                    placeholder="Meta Description"
                    name="seo"
                    data-name="description"
                    className="input mt-4"
                    value={blogFormData?.seo?.description}
                    onChange={handleBlogSeo}
                  />
                </div>
              </div>
            </div>

            {isError && (
              <Alert
                onClose={closeErrorMessage}
                className="mb-4"
                severity="error"
                sx={{ width: "100%" }}
              >
                Error creating blog, Please try again later
              </Alert>
            )}

            {isSuccess && (
              <Alert
                onClose={closeErrorMessage}
                className="mb-4"
                severity="success"
                sx={{ width: "100%" }}
              >
                Blog Created Successfully
              </Alert>
            )}

            <div className="text-center my-6">
              {isSubmitting ? (
                <Spinner />
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  className="primaryBtn-contained"
                >
                  Publish Blog
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}

export default AddBlogs;
